'use client';
import { client } from '@/sanity/lib/client';
import { GET_LEVEL_1_PRODUCTS } from '@/sanity/lib/queries';
import { useEffect, useState } from 'react';

type UseCheckSlugsResult = {
  loading: boolean;
  error: string | null;
  type: string | null;
};

/**
 * Checks the document type for a given slug.
 * Returns `_type` of the document that owns the slug, or null if not found.
 */
export const useCheckSlugs = (slug?: string): UseCheckSlugsResult => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTypeBySlug = async () => {
      if (!slug) {
        setLoading(false);
        setType(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Use existing level-1 products query and infer type by membership
        const level1Products = await client.fetch<Array<{ slug?: string }>>(GET_LEVEL_1_PRODUCTS);
        if (!isMounted) return;
        const isLevel1 = (level1Products || []).some((p) => p?.slug === slug);
        setType(isLevel1 ? 'structaProductLevel1' : 'productsList');
      } catch (e: any) {
        if (!isMounted) return;
        setError(e?.message ?? 'Failed to fetch slug type');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTypeBySlug();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  return { loading, error, type };
};
